import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapPin, Buildings, Plus, Trash, PencilSimple, ArrowLeft, Sparkle, ArrowsClockwise, CheckCircle, WarningCircle, Star, ArrowsMerge } from "@phosphor-icons/react";

const inputCls = "bg-surface-2 border-border text-fg";
const OFFICE_TYPE_LABELS = { HQ: "Siège", OFFICE: "Bureau", BRANCH: "Filiale", AGENCY: "Agence", OTHER: "Autre" };
const OFFICE_TYPE_OPTIONS = ["OFFICE", "BRANCH", "AGENCY", "OTHER"];

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
}

function OfficeForm({ initial, onCancel, onSaved, companyId }) {
  const editing = !!initial?.id;
  const isHQ = !!initial?.is_headquarters;
  const [form, setForm] = useState({
    name: initial?.name || "",
    city: initial?.city || "",
    region: initial?.region || "",
    office_type: initial?.office_type && OFFICE_TYPE_OPTIONS.includes(initial.office_type) ? initial.office_type : "AGENCY",
    coverage_radius_km: initial?.coverage_radius_km || 30,
  });
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async () => {
    if (!form.city.trim()) { toast.error("La ville est requise"); return; }
    setBusy(true);
    try {
      const payload = { ...form, coverage_radius_km: Number(form.coverage_radius_km) || 30 };
      if (isHQ) delete payload.office_type; // ne jamais déclasser le siège
      if (editing) await api.put(`/admin/offices/${initial.id}`, payload);
      else await api.post(`/admin/companies/${companyId}/offices`, payload);
      toast.success(editing ? "Implantation modifiée" : "Implantation créée");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="surface rounded-lg p-4 space-y-3 border border-accent/30" data-testid="office-form">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="overline block mb-1.5">Ville *</label>
          <Input className={inputCls} value={form.city} onChange={upd("city")} placeholder="Lille" data-testid="office-form-city" />
        </div>
        <div>
          <label className="overline block mb-1.5">Région</label>
          <Input className={inputCls} value={form.region} onChange={upd("region")} placeholder="Hauts-de-France" data-testid="office-form-region" />
        </div>
        <div>
          <label className="overline block mb-1.5">Type</label>
          {isHQ ? (
            <div className="h-9 flex items-center px-3 rounded-md bg-surface-2 border border-border text-sm text-muted" data-testid="office-form-type">Siège (non modifiable)</div>
          ) : (
            <Select value={form.office_type} onValueChange={upd("office_type")}>
              <SelectTrigger className={inputCls + " h-9"} data-testid="office-form-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {OFFICE_TYPE_OPTIONS.map((t) => <SelectItem key={t} value={t}>{OFFICE_TYPE_LABELS[t]}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
        <div>
          <label className="overline block mb-1.5">Rayon de couverture (km)</label>
          <Input type="number" className={inputCls} value={form.coverage_radius_km} onChange={upd("coverage_radius_km")} data-testid="office-form-radius" />
        </div>
      </div>
      <div>
        <label className="overline block mb-1.5">Nom (optionnel)</label>
        <Input className={inputCls} value={form.name} onChange={upd("name")} placeholder="Agence Lille" data-testid="office-form-name" />
      </div>
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="px-4 py-2 rounded-md text-sm text-muted hover:text-fg" data-testid="office-form-cancel">Annuler</button>
        <button onClick={submit} disabled={busy} data-testid="office-form-submit"
          className="bg-accent text-black font-semibold px-4 py-2 rounded-md text-sm hover-lift disabled:opacity-60">
          {busy ? "…" : editing ? "Enregistrer" : "Créer l'implantation"}
        </button>
      </div>
    </div>
  );
}

function ReassignView({ office, companyId, onBack, onDone }) {
  const [loading, setLoading] = useState(true);
  const [signals, setSignals] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [reenrich, setReenrich] = useState(true);
  const [busy, setBusy] = useState(false);

  const loadMatches = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/admin/offices/${office.id}/match-signals`);
      setSignals(data.signals || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [office.id]);

  useEffect(() => { loadMatches(); }, [loadMatches]);

  const toggle = (id) => setSelected((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const assign = async (mode) => {
    const body = { reenrich };
    if (mode === "all") body.all = true;
    else if (mode === "high") body.high_confidence = true;
    else {
      if (!selected.size) { toast.error("Sélectionnez au moins un signal"); return; }
      body.signal_ids = [...selected];
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/offices/${office.id}/assign-signals`, body);
      toast.success(`${data.assigned} signal(aux) réaffecté(s) à ${office.name}${data.reenriched ? " · enrichissement relancé" : ""}`);
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const highCount = signals.filter((s) => s.confidence >= 0.8).length;

  return (
    <div className="space-y-4" data-testid="office-reassign-view">
      <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-accent hover:underline" data-testid="office-reassign-back">
        <ArrowLeft size={15} /> Retour aux implantations
      </button>
      <div>
        <div className="font-display font-semibold text-lg flex items-center gap-2"><MapPin size={18} className="text-accent" /> Réaffecter des signaux → {office.name}</div>
        <p className="text-xs text-muted mt-1">Signaux de la société correspondant géographiquement à cette implantation ({office.city}{office.region ? `, ${office.region}` : ""}).</p>
      </div>

      <div className="flex items-center justify-between gap-2 flex-wrap surface rounded-lg p-3">
        <label className="flex items-center gap-2 text-sm text-fg">
          <Switch checked={reenrich} onCheckedChange={setReenrich} data-testid="office-reenrich-switch" />
          Relancer l'enrichissement des décideurs locaux
        </label>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => assign("high")} disabled={busy || !highCount} data-testid="office-assign-high"
            className="border border-border text-fg text-xs font-semibold px-3 py-2 rounded-md hover:border-accent/40 disabled:opacity-50">
            Haute confiance ({highCount})
          </button>
          <button onClick={() => assign("all")} disabled={busy || !signals.length} data-testid="office-assign-all"
            className="border border-border text-fg text-xs font-semibold px-3 py-2 rounded-md hover:border-accent/40 disabled:opacity-50">
            Tout réaffecter ({signals.length})
          </button>
          <button onClick={() => assign("selection")} disabled={busy || !selected.size} data-testid="office-assign-selection"
            className="bg-accent text-black text-xs font-semibold px-3 py-2 rounded-md hover-lift disabled:opacity-50">
            Réaffecter la sélection ({selected.size})
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-sm text-muted py-8" data-testid="office-match-loading">Analyse des signaux…</div>
      ) : signals.length === 0 ? (
        <div className="text-center text-sm text-muted py-8" data-testid="office-match-empty">Aucun signal à réaffecter pour cette implantation.</div>
      ) : (
        <div className="surface rounded-lg divide-y divide-border/60 max-h-72 overflow-y-auto" data-testid="office-match-list">
          {signals.map((s) => (
            <label key={s.id} className="flex items-center gap-3 px-3 py-2.5 hover:bg-surface-2/40 cursor-pointer" data-testid={`office-match-row-${s.id}`}>
              <input type="checkbox" checked={selected.has(s.id)} onChange={() => toggle(s.id)}
                className="accent-[#00E5FF]" data-testid={`office-match-check-${s.id}`} />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-fg truncate">{s.role_title || "—"}</div>
                <div className="text-xs text-muted">{s.city || "—"}{s.region ? ` · ${s.region}` : ""}{s.current_office_id ? " · déjà rattaché" : ""}</div>
              </div>
              <span className={`text-xs font-mono px-2 py-0.5 rounded ${s.confidence >= 0.8 ? "bg-accent/15 text-accent" : "bg-surface-2 text-muted"}`}>
                {Math.round(s.confidence * 100)}%
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

export default function OfficesDialog({ company, open, onClose }) {
  const [offices, setOffices] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editOffice, setEditOffice] = useState(null);
  const [reassignOffice, setReassignOffice] = useState(null);
  const [prefillCity, setPrefillCity] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [mergeMode, setMergeMode] = useState(false);
  const [mergeSel, setMergeSel] = useState(new Set());
  const [mergeTarget, setMergeTarget] = useState(null);
  const [mergeBusy, setMergeBusy] = useState(false);

  const companyId = company?.id;

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const [o, s] = await Promise.all([
        api.get(`/admin/companies/${companyId}/offices`),
        api.get(`/admin/companies/${companyId}/office-suggestions`),
      ]);
      setOffices((o.data.offices || []).filter((x) => x.is_active !== false));
      setSuggestions(s.data.suggestions || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [companyId]);

  useEffect(() => {
    if (open) { setReassignOffice(null); setShowForm(false); setEditOffice(null); setPrefillCity(null); setMergeMode(false); setMergeSel(new Set()); load(); }
  }, [open, load]);

  const setHeadquarters = async (o) => {
    if (!window.confirm(`Définir « ${o.name} » comme siège social ? L'ancien siège deviendra une antenne et la ville « siège » de la société sera mise à jour.`)) return;
    try {
      await api.post(`/admin/offices/${o.id}/set-headquarters`);
      toast.success(`« ${o.city} » est désormais le siège`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const exitMerge = () => { setMergeMode(false); setMergeSel(new Set()); setMergeTarget(null); };

  const toggleMergeSel = (id) => setMergeSel((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    if (!n.has(mergeTarget)) setMergeTarget([...n][0] || null);
    return n;
  });

  const doMerge = async () => {
    const ids = [...mergeSel];
    const target = mergeTarget || ids[0];
    const sources = ids.filter((x) => x !== target);
    if (!target || sources.length === 0) { toast.error("Sélectionnez au moins 2 implantations"); return; }
    const targetName = offices.find((o) => o.id === target)?.name || "";
    if (!window.confirm(`Fusionner ${sources.length} implantation(s) dans « ${targetName} » ? Les signaux seront déplacés et les autres implantations supprimées.`)) return;
    setMergeBusy(true);
    try {
      const { data } = await api.post(`/admin/offices/merge`, { target_office_id: target, source_office_ids: sources });
      toast.success(`${data.merged} implantation(s) fusionnée(s) · ${data.signals_moved} signal(aux) déplacé(s)${data.target_became_hq ? " · nouvelle cible = siège" : ""}`);
      exitMerge();
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setMergeBusy(false); }
  };

  const remove = async (o) => {
    if (!window.confirm(`Supprimer l'implantation « ${o.name} » ? Les signaux rattachés seront détachés (l'historique est conservé).`)) return;
    try {
      await api.delete(`/admin/offices/${o.id}`);
      toast.success("Implantation supprimée");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const createFromSuggestion = (sugg) => {
    setEditOffice(null);
    setPrefillCity({ city: sugg.display_city, region: sugg.region });
    setShowForm(true);
  };

  const createAllSuggestions = async () => {
    setBulkBusy(true);
    try {
      const { data } = await api.post(`/admin/companies/${companyId}/offices/bulk-create`, { reenrich: true });
      toast.success(`${data.offices_created} implantation(s) créée(s) · ${data.signals_assigned} signal(aux) rattaché(s)${data.reenriched ? " · enrichissement relancé" : ""}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBulkBusy(false); }
  };

  const closeForm = () => { setShowForm(false); setEditOffice(null); setPrefillCity(null); };
  const onSaved = () => { closeForm(); load(); };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="bg-surface border-border max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="offices-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Buildings size={20} className="text-accent" /> Implantations — {company?.name}</DialogTitle>
          <DialogDescription className="text-muted">
            La géographie du besoin (implantation) prime sur celle du siège pour la recherche de décideurs.
          </DialogDescription>
          {company?.siren && (
            <div className="text-xs font-mono text-muted mt-1" data-testid="offices-company-siren">
              SIREN entreprise : <span className="text-emerald-300">{company.siren}</span>
            </div>
          )}
        </DialogHeader>

        {reassignOffice ? (
          <ReassignView office={reassignOffice} companyId={companyId}
            onBack={() => setReassignOffice(null)}
            onDone={() => { setReassignOffice(null); load(); }} />
        ) : (
          <div className="space-y-4">
            {!showForm && (
              <div className="flex justify-end gap-2">
                {offices.length >= 2 && (
                  <button onClick={() => { mergeMode ? exitMerge() : setMergeMode(true); }} data-testid="office-merge-toggle"
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-semibold border ${mergeMode ? "bg-accent/12 border-accent/40 text-accent" : "border-border text-fg hover:border-accent/40"}`}>
                    <ArrowsMerge size={15} /> {mergeMode ? "Annuler la fusion" : "Fusionner"}
                  </button>
                )}
                <button onClick={() => { setEditOffice(null); setPrefillCity(null); setShowForm(true); }} data-testid="office-create-btn"
                  className="flex items-center gap-1.5 bg-accent text-black font-semibold px-3 py-2 rounded-md text-sm hover-lift">
                  <Plus size={15} /> Nouvelle implantation
                </button>
              </div>
            )}

            {showForm && (
              <OfficeForm key={editOffice?.id || prefillCity?.city || "new"} companyId={companyId}
                initial={editOffice || prefillCity} onCancel={closeForm} onSaved={onSaved} />
            )}

            {suggestions.length > 0 && (
              <div className="surface rounded-lg p-3" data-testid="office-suggestions">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="overline flex items-center gap-1.5"><Sparkle size={13} className="text-accent" /> Villes suggérées (≥3 signaux sans implantation)</div>
                  {suggestions.length >= 1 && (
                    <button onClick={createAllSuggestions} disabled={bulkBusy} data-testid="office-create-all"
                      className="flex items-center gap-1.5 bg-accent text-black text-xs font-semibold px-3 py-1.5 rounded-full hover-lift disabled:opacity-60">
                      <Plus size={12} /> {bulkBusy ? "Création…" : `Tout créer (${suggestions.length}) + réaffecter`}
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {suggestions.map((s) => (
                    <button key={s.normalized_city} onClick={() => createFromSuggestion(s)}
                      data-testid={`office-suggestion-${s.normalized_city.replace(/\s+/g, "-")}`}
                      className="flex items-center gap-1.5 border border-accent/30 text-fg text-xs px-3 py-1.5 rounded-full hover:bg-accent/10">
                      <Plus size={12} /> {s.display_city} <span className="text-muted font-mono">({s.count})</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {loading ? (
              <div className="text-center text-sm text-muted py-8" data-testid="offices-loading">Chargement…</div>
            ) : offices.length === 0 ? (
              <div className="text-center text-sm text-muted py-8" data-testid="offices-empty">Aucune implantation. Le siège apparaîtra ici si l'entreprise a une ville renseignée.</div>
            ) : (
              <div className="space-y-2" data-testid="offices-list">
                {mergeMode && (
                  <div className="text-xs text-accent bg-accent/8 border border-accent/25 rounded-md px-3 py-2" data-testid="merge-hint">
                    Cochez 2 implantations ou plus, choisissez celle à conserver (◉), puis fusionnez.
                  </div>
                )}
                {offices.map((o) => (
                  <div key={o.id} className={`surface rounded-lg p-3 flex items-start justify-between gap-3 ${mergeMode && mergeSel.has(o.id) ? "ring-1 ring-accent/50" : ""}`} data-testid={`office-row-${o.id}`}>
                    <div className="flex items-start gap-2 min-w-0">
                      {mergeMode && (
                        <input type="checkbox" checked={mergeSel.has(o.id)} onChange={() => toggleMergeSel(o.id)}
                          className="mt-1 accent-[#00E5FF]" data-testid={`office-merge-check-${o.id}`} />
                      )}
                      <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-fg">{o.name}</span>
                        {o.is_headquarters ? (
                          <span className="text-[10px] font-bold uppercase tracking-wide bg-accent/15 text-accent px-2 py-0.5 rounded">Siège</span>
                        ) : (
                          <span className="text-[10px] font-bold uppercase tracking-wide bg-surface-2 text-muted px-2 py-0.5 rounded">{OFFICE_TYPE_LABELS[o.office_type] || "Agence"}</span>
                        )}
                        {mergeMode && mergeSel.has(o.id) && (
                          <button onClick={() => setMergeTarget(o.id)} data-testid={`office-merge-target-${o.id}`}
                            className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${mergeTarget === o.id ? "bg-accent text-black border-accent" : "text-accent border-accent/40 hover:bg-accent/10"}`}>
                            {mergeTarget === o.id ? "◉ à conserver" : "○ conserver"}
                          </button>
                        )}
                        {!o.is_active && <span className="text-[10px] text-destructive">inactive</span>}
                      </div>
                      <div className="text-xs text-muted mt-1 flex items-center gap-1"><MapPin size={12} /> {o.city}{o.region ? `, ${o.region}` : ""} · {o.coverage_radius_km || 30} km</div>
                      <div className="text-xs text-muted mt-1 font-mono">{o.signals || 0} signaux · {o.decision_makers || 0} décideurs · dernier : {fmtDate(o.last_signal_at)}</div>
                      <div className="text-xs mt-1 font-mono flex items-center gap-1.5" data-testid={`office-siret-${o.id}`}>
                        <span className="text-muted">SIRET</span>
                        {o.siret ? (
                          <>
                            <span className={company?.siren && o.siret.startsWith(company.siren) ? "text-emerald-300" : "text-amber-300"}>{o.siret}</span>
                            {company?.siren && (o.siret.startsWith(company.siren)
                              ? <CheckCircle size={13} weight="fill" className="text-emerald-400" title="Cohérent avec le SIREN de l'entreprise" />
                              : <WarningCircle size={13} weight="fill" className="text-amber-400" title="Ne correspond pas au SIREN de l'entreprise" />)}
                          </>
                        ) : (
                          <span className="text-muted" title="Établissement sans SIRET officiel (créé manuellement)">— (non INSEE)</span>
                        )}
                      </div>
                      </div>
                    </div>
                    {!mergeMode && (
                    <div className="flex items-center gap-1 shrink-0">
                      {!o.is_headquarters && (
                        <button onClick={() => setHeadquarters(o)} title="Définir comme siège" data-testid={`office-set-hq-${o.id}`}
                          className="p-2 rounded-md text-muted hover:text-accent hover:bg-surface-2"><Star size={14} /></button>
                      )}
                      <button onClick={() => setReassignOffice(o)} data-testid={`office-reassign-${o.id}`}
                        className="flex items-center gap-1 text-xs text-accent border border-accent/30 px-2 py-1.5 rounded-md hover:bg-accent/10">
                        <ArrowsClockwise size={13} /> Réaffecter
                      </button>
                      <button onClick={() => { setPrefillCity(null); setEditOffice(o); setShowForm(true); }} data-testid={`office-edit-${o.id}`}
                        className="p-2 rounded-md text-muted hover:text-accent hover:bg-surface-2"><PencilSimple size={14} /></button>
                      {!o.is_headquarters && (
                        <button onClick={() => remove(o)} data-testid={`office-delete-${o.id}`}
                          className="p-2 rounded-md text-muted hover:text-destructive hover:bg-surface-2"><Trash size={14} /></button>
                      )}
                    </div>
                    )}
                  </div>
                ))}
                {mergeMode && mergeSel.size >= 2 && (
                  <div className="sticky bottom-0 bg-surface border border-accent/30 rounded-md px-3 py-2.5 flex items-center justify-between gap-2 flex-wrap" data-testid="merge-bar">
                    <span className="text-xs text-fg">Conserver <b className="text-accent">{offices.find((o) => o.id === (mergeTarget || [...mergeSel][0]))?.name}</b> · fusionner {mergeSel.size - 1} autre(s)</span>
                    <button onClick={doMerge} disabled={mergeBusy} data-testid="office-merge-confirm"
                      className="flex items-center gap-1.5 bg-accent text-black text-xs font-semibold px-4 py-2 rounded-md hover-lift disabled:opacity-60">
                      <ArrowsMerge size={14} /> {mergeBusy ? "Fusion…" : `Fusionner (${mergeSel.size})`}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
